// A screen's code is fetched the first time it is opened. Pointing at, or
// tabbing to, its sidebar row is a strong hint it is about to be, so the row
// asks the browser to prefetch that screen's files into the HTTP cache.
//
// Plain `<link rel="prefetch">`, never `import()`: a prefetch that fails
// leaves nothing behind (no module-map entry, no Vite preload record, no
// `vite:preloadError`), so the real navigation fetches and recovers exactly as
// it would have without the hint. The URLs come from the build
// (route-preload-map.ts); in the dev server there is no map and this does
// nothing.

const MAP_ID = "route-preload-map"

let map: Record<string, string[]> | null | undefined
const started = new Set<string>()
// Vite skips its own modulepreload for any file that already has a
// `link[href]`, a prefetch included, so a link left in the page would make
// the click load the screen one import level at a time. Each link goes once
// it has loaded or failed, and a screen's pending ones go when it is opened.
const pending = new Map<string, HTMLLinkElement[]>()

const readMap = (): Record<string, string[]> | null => {
  if (map !== undefined) return map
  try {
    const text = document.getElementById(MAP_ID)?.textContent
    map = text ? (JSON.parse(text) as Record<string, string[]>) : null
  } catch {
    map = null
  }
  return map
}

const readMapIfPresent = () =>
  typeof document === "undefined" ? null : readMap()

const normalize = (path: string) => `/${path.replace(/^\/+|\/+$/g, "")}`

const routeFor = (pathname: string, routes: string[]): string | null => {
  const target = normalize(pathname)
  let best: string | null = null
  for (const route of routes) {
    const matches = target === route || target.startsWith(`${route}/`)
    if (matches && (!best || route.length > best.length)) best = route
  }
  return best
}

const alreadyRequested = (url: string) =>
  [...document.head.querySelectorAll("link, script")].some(
    (node) =>
      node.getAttribute("href") === url || node.getAttribute("src") === url
  )

/** Prefetches the screen at `pathname`, once per page load; never offline. */
export const preloadRoute = (pathname: string): void => {
  if (typeof document === "undefined") return
  if (typeof navigator !== "undefined" && navigator.onLine === false) return
  const routes = readMap()
  if (!routes) return
  const route = routeFor(pathname, Object.keys(routes))
  if (!route || started.has(route)) return
  started.add(route)

  const links: HTMLLinkElement[] = []
  pending.set(route, links)
  for (const url of routes[route] ?? []) {
    if (alreadyRequested(url)) continue
    const link = document.createElement("link")
    const done = () => {
      link.remove()
      const index = links.indexOf(link)
      if (index >= 0) links.splice(index, 1)
    }
    link.addEventListener("load", done, { once: true })
    link.addEventListener("error", done, { once: true })
    links.push(link)
    link.rel = "prefetch"
    // Module scripts and Vite's stylesheets are requested in CORS mode; the
    // prefetch must match for the browser to reuse it.
    link.crossOrigin = ""
    link.href = url
    document.head.appendChild(link)
  }
}

/** Drops the screen's still-pending prefetch links; call as navigation to it starts. */
export const releaseRoutePrefetch = (pathname: string): void => {
  const routes = readMapIfPresent()
  if (!routes) return
  const route = routeFor(pathname, Object.keys(routes))
  if (!route) return
  for (const link of pending.get(route) ?? []) link.remove()
  pending.delete(route)
}

/** Test seam: forget the parsed map and what was prefetched. */
export const resetRoutePreloadForTests = () => {
  map = undefined
  started.clear()
  pending.clear()
}
